<template>
  <div class="MermaidPreviewControls">
    <div class="MermaidPreviewControls__zoom" role="group" aria-label="Zoom">
      <UITooltip placement="bottom" class="MermaidPreviewControls__tooltip">
        <template #trigger>
          <UIButton
            type="button"
            theme="secondary"
            size="md"
            class="MermaidPreviewControls__zoomBtn MermaidPreviewControls__zoomBtn--first"
            aria-label="Zoom out"
            :isDisabled="!canZoomOut"
            @click="emit('zoom-out')"
          >
            −
          </UIButton>
        </template>
        <template #content>
          Zoom out in steps of 25%. Minimum zoom is 25%.
        </template>
      </UITooltip>
      <span class="MermaidPreviewControls__zoomPct" aria-live="polite">
        {{ zoomPercent }}%
      </span>
      <UITooltip placement="bottom" class="MermaidPreviewControls__tooltip">
        <template #trigger>
          <UIButton
            type="button"
            theme="secondary"
            size="md"
            class="MermaidPreviewControls__zoomBtn"
            aria-label="Zoom in"
            :isDisabled="!canZoomIn"
            @click="emit('zoom-in')"
          >
            +
          </UIButton>
        </template>
        <template #content>
          Zoom in in steps of 25%. Maximum zoom is 500%.
        </template>
      </UITooltip>
    </div>

    <UITooltip placement="bottom" class="MermaidPreviewControls__tooltip">
      <template #trigger>
        <UIButton
          type="button"
          theme="secondary"
          size="md"
          class="MermaidPreviewControls__toolBtn"
          aria-label="Fit diagram to preview"
          @click="emit('fit-view')"
        >
          <template #icon-before>
            <span class="MermaidPreviewControls__iconSvg" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M2 5V2H5M11 2H14V5M14 11V14H11M5 14H2V11"
                  stroke="currentColor"
                  stroke-width="1.25"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </template>
        </UIButton>
      </template>
      <template #content>
        Resize the diagram so it fits entirely inside the preview, with a small
        margin.
      </template>
    </UITooltip>

    <UITooltip placement="bottom" class="MermaidPreviewControls__tooltip">
      <template #trigger>
        <UIButton
          type="button"
          theme="secondary"
          size="md"
          class="MermaidPreviewControls__toolBtn"
          :aria-label="
            viewportLocked
              ? 'Unlock preview scrolling'
              : 'Lock preview scrolling'
          "
          @click="emit('toggle-viewport-lock')"
        >
          <template #icon-before>
            <UIIcon
              name="lock"
              class="MermaidPreviewControls__uiIcon"
              :class="{
                'MermaidPreviewControls__uiIcon--muted': !viewportLocked,
              }"
            />
          </template>
        </UIButton>
      </template>
      <template #content>
        <span v-if="viewportLocked">
          Unlock the preview so you can scroll the diagram again.
        </span>
        <span v-else>
          Lock the preview to stop scrolling and interacting with the diagram
          (read-only view).
        </span>
      </template>
    </UITooltip>

    <UITooltip placement="bottom" class="MermaidPreviewControls__tooltip">
      <template #trigger>
        <UIButton
          type="button"
          theme="secondary"
          size="md"
          class="MermaidPreviewControls__toolBtn"
          aria-label="Reset zoom to initial fit"
          @click="emit('reset-zoom')"
        >
          <template #icon-before>
            <UIIcon name="refresh" class="MermaidPreviewControls__uiIcon" />
          </template>
        </UIButton>
      </template>
      <template #content>
        Restore zoom to the level from when this diagram finished loading (fit
        to preview).
      </template>
    </UITooltip>

    <UITooltip placement="bottom" class="MermaidPreviewControls__tooltip">
      <template #trigger>
        <UIButton
          type="button"
          theme="secondary"
          size="md"
          class="MermaidPreviewControls__toolBtn"
          aria-label="Zoom to 100 percent"
          @click="emit('zoom-to-100')"
        >
          <template #icon-before>
            <span class="MermaidPreviewControls__iconSvg" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M5.5 3L3 5.5M11 3L13.5 5.5M3 10.5L5.5 13M13.5 10.5L11 13M8 4V12M4 8H12"
                  stroke="currentColor"
                  stroke-width="1.15"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </template>
        </UIButton>
      </template>
      <template #content>
        Set zoom to 100% so the SVG draws at its native pixel dimensions (may
        require scrolling).
      </template>
    </UITooltip>

    <UIDropdown
      class="MermaidPreviewControls__settings"
      :items="downloadOptions"
      placement="bottom-end"
    >
      <template #toggle-container>
        <UITooltip placement="bottom" class="MermaidPreviewControls__tooltip">
          <template #trigger>
            <UIButton
              type="button"
              theme="secondary"
              size="md"
              class="MermaidPreviewControls__toolBtn"
              aria-label="Download options"
            >
              <template #icon-before>
                <UIIcon
                  name="download"
                  class="MermaidPreviewControls__uiIcon"
                />
              </template>
            </UIButton>
          </template>
          <template #content>
            Open the menu to download this diagram as an SVG vector file or a
            PNG image.
          </template>
        </UITooltip>
      </template>
    </UIDropdown>
  </div>
</template>

<script lang="ts" setup>
import { UIButton, UIDropdown, UITooltip } from '@foundever/ui-lib'

import type { IDropdownItem } from '@/views/components/ui/dropdowns/types'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'

defineProps<{
  zoomPercent: number
  canZoomIn: boolean
  canZoomOut: boolean
  viewportLocked: boolean
  downloadOptions: IDropdownItem[]
}>()

const emit = defineEmits<{
  'zoom-in': []
  'zoom-out': []
  'fit-view': []
  'reset-zoom': []
  'zoom-to-100': []
  'toggle-viewport-lock': []
}>()
</script>

<style lang="scss" scoped>
.MermaidPreviewControls {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.MermaidPreviewControls__tooltip {
  display: inline-flex;
  align-self: stretch;
  align-items: stretch;
}

.MermaidPreviewControls__zoom {
  display: flex;
  align-items: stretch;
  border-radius: var(--radius-s);
  overflow: hidden;
  box-shadow:
    0 1px 4px rgba(15, 23, 42, 0.08),
    0 1px 2px rgba(15, 23, 42, 0.06);
  background: var(--bg-elevated-primary, var(--bg-primary));
}

.MermaidPreviewControls__zoomBtn {
  min-width: 2rem;
  width: 2rem;
  height: 2rem;
  padding: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  font-size: 1.05rem !important;
  font-weight: 500;
  line-height: 1;
  color: var(--text-primary) !important;
  box-shadow: none !important;

  &:hover:not([data-is-disabled='true']) {
    background: var(--interactive-bg-secondary-hover) !important;
    color: var(--text-primary) !important;
  }

  &[data-is-disabled='true'] {
    opacity: 0.45;
  }
}

.MermaidPreviewControls__zoomBtn--first {
  border-right: 1px solid var(--border-heavy) !important;
}

.MermaidPreviewControls__zoomPct {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.75rem;
  padding: 0 0.35rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-primary);
  user-select: none;
  border-right: 1px solid var(--border-heavy);
}

.MermaidPreviewControls__toolBtn {
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  padding: 0 !important;
  border: none !important;
  border-radius: var(--radius-s) !important;
  color: var(--text-primary) !important;
  box-shadow:
    0 1px 4px rgba(15, 23, 42, 0.08),
    0 1px 2px rgba(15, 23, 42, 0.06);

  &:hover:not([data-is-disabled='true']) {
    background: var(--interactive-bg-secondary-hover) !important;
    color: var(--text-primary) !important;
  }
}

.MermaidPreviewControls__iconSvg {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--icon-primary);
}

.MermaidPreviewControls__uiIcon {
  --color: var(--icon-primary);
}

.MermaidPreviewControls__uiIcon--muted {
  opacity: 0.45;
}

.MermaidPreviewControls__settings {
  display: inline-flex;
}
</style>
